import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import AddressAutocomplete from '../components/AddressAutocomplete';
import FacturesSection from '../components/FacturesSection';
import ImportantNotesBanner from '../components/ImportantNotesBanner';
import ImportantNotesButton from '../components/ImportantNotesButton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, DialogHeader } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useResilientSave, isNetworkError } from '../components/OfflineSavePrompt';
import {
    ArrowLeft, Clock, Users, FileText, MapPin, ChevronDown, MoreHorizontal, Download,
    Eye, DollarSign, CheckCircle2, Plus, Trash2, Pencil, RefreshCw, CalendarDays, Minus, Save,
    Package, Truck, Euro, Shield, ArrowUpDown, Warehouse, Upload, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
    en_attente: { label: 'En attente', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
    programme: { label: 'Programmee', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    stocke: { label: 'Stocke', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
    termine: { label: 'Terminee', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
};

const ASCENSEUR_OPTIONS = [
    { value: 'non', label: 'Non' },
    { value: '1_3', label: '1 a 3 personnes' },
    { value: '4_6', label: '4 a 6 personnes' },
    { value: '7_9', label: '7 a 9 personnes' },
    { value: '10_plus', label: '10 personnes et plus' },
    { value: 'indetermine', label: 'Indetermine' },
    { value: 'interdit', label: 'Interdit' },
];

const FORMULE_LABELS = {
    economique: 'Economique', economique_plus: 'Economique Plus',
    standard: 'Standard', luxe: 'Luxe',
};

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canWrite: canWriteClient, canDelete: canDeleteClient } = useCan('clients');
    const [client, setClient] = useState(null);
    // Day-checkboxes confirmation dialog
    const [dayDialog, setDayDialog] = useState({ open: false, kind: '', label: '', date: '', jourIndex: 0, willEnable: true, busy: false });
    const [loading, setLoading] = useState(true);
    const [statutDropOpen, setStatutDropOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [apercuOpen, setApercuOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);

    // Editable fields for the apercu modal
    const [editPrix, setEditPrix] = useState('');
    const [editPrestations, setEditPrestations] = useState([]);
    const [editRemarques, setEditRemarques] = useState('');
    const [editFields, setEditFields] = useState({});
    const [presDropOpen, setPresDropOpen] = useState(false);
    const [customPresName, setCustomPresName] = useState('');
    const [showCustomInput, setShowCustomInput] = useState(false);

    const PRESTATION_OPTIONS = ['Garde meuble', 'Autorisation stationnement', 'Monte meuble', 'Dechetterie'];

    // Programmation state
    const [progEditMode, setProgEditMode] = useState(false);
    const [progNbJours, setProgNbJours] = useState(0);
    const [progJours, setProgJours] = useState([]);
    const [progSaving, setProgSaving] = useState(false);
    const resilient = useResilientSave();

    const TYPE_PRESTATION_OPTIONS = [
        'Preparation',
        'Preparation et chargement',
        'Chargement',
        'Dechargement',
        'Stockage',
        'Sortie garde meuble',
        'Route sur 1 journee',
        'Demenagement local',
    ];

    // Livraison state
    const [livEditMode, setLivEditMode] = useState(false);
    const [livFournitures, setLivFournitures] = useState({ cartons_standards: 0, cartons_livres: 0, adhesif: 0, papier_bulle: 0 });
    const [livProgMode, setLivProgMode] = useState(false);
    const [livDate, setLivDate] = useState('');
    const [livHeure, setLivHeure] = useState('');
    const [livRemarqueModal, setLivRemarqueModal] = useState(false);
    const [livRemarque, setLivRemarque] = useState('');
    const [livSaving, setLivSaving] = useState(false);

    // Upload dialog for AOT/MM/GM "accorde" status
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadContext, setUploadContext] = useState(null); // { section: 'aot'|'monte-meuble'|'garde-meuble', type: 'depart'|'arrivee' }
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadSaving, setUploadSaving] = useState(false);

    const AOT_STATUT_OPTIONS = [
        { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
        { value: 'demande', label: 'Demande', dot: 'bg-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
        { value: 'refuse', label: 'Refuse', dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600' },
        { value: 'accorde', label: 'Accorde', dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
        { value: 'hors_delai', label: 'Hors delai', dot: 'bg-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' },
    ];

    const MM_STATUT_OPTIONS = [
        { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
        { value: 'reserve', label: 'Reserve', dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    ];

    const GM_STATUT_OPTIONS = [
        { value: 'en_attente', label: 'En attente', dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
        { value: 'entree_reservee', label: 'Entree reservee', dot: 'bg-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
        { value: 'sortie_planifiee', label: 'Sortie planifiee', dot: 'bg-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    ];

    // Track which dropdown is open: 'aot-depart', 'aot-arrivee', 'mm-depart', etc.
    const [openStatutDrop, setOpenStatutDrop] = useState(null);

    // Acompte state
    const [acompteFormOpen, setAcompteFormOpen] = useState(false);
    const [acompteDate, setAcompteDate] = useState('');
    const [acompteMontant, setAcompteMontant] = useState('');
    const [acompteType, setAcompteType] = useState('');
    const [acompteRemarque, setAcompteRemarque] = useState('Acompte');
    const [acompteSaving, setAcompteSaving] = useState(false);

    // Cancel client state
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelSaving, setCancelSaving] = useState(false);

    // Solde edit/create state (terrain encaisse)
    const [soldeEdit, setSoldeEdit] = useState(null); // {id?, date, montant, type_paiement, remarque}
    const [soldeSaving, setSoldeSaving] = useState(false);
    const TYPE_PAIEMENT_SOLDE = ['Cheque', 'Virement', 'Espece', 'CB'];

    const openSoldeEdit = (sd) => {
        setSoldeEdit({
            id: sd.id,
            date: sd.date || '',
            montant: sd.montant != null ? String(sd.montant) : '',
            type_paiement: sd.type_paiement || '',
            remarque: sd.remarque || '',
        });
    };

    const openSoldeCreate = () => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        setSoldeEdit({ id: null, date: `${y}-${m}-${d}`, montant: '', type_paiement: '', remarque: '' });
    };

    const handleSoldeSave = async () => {
        if (!soldeEdit) return;
        const m = parseFloat(soldeEdit.montant);
        if (!soldeEdit.date) { toast.error('Date requise'); return; }
        if (isNaN(m) || m <= 0) { toast.error('Montant invalide'); return; }
        if (!soldeEdit.type_paiement) { toast.error('Type de paiement requis'); return; }
        setSoldeSaving(true);
        try {
            const payload = { date: soldeEdit.date, montant: m, type_paiement: soldeEdit.type_paiement, remarque: soldeEdit.remarque };
            if (soldeEdit.id) {
                await axios.patch(`${API_URL}/api/clients/${id}/soldes/${soldeEdit.id}`, payload, { withCredentials: true });
                toast.success('Solde mis a jour');
            } else {
                await axios.post(`${API_URL}/api/clients/${id}/soldes`, payload, { withCredentials: true });
                toast.success('Paiement enregistre');
            }
            setSoldeEdit(null);
            fetchClient();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSoldeSaving(false);
        }
    };

    const handleSoldeDelete = async (sid) => {
        if (!window.confirm('Supprimer ce solde encaisse ?')) return;
        try {
            await axios.delete(`${API_URL}/api/clients/${id}/soldes/${sid}`, { withCredentials: true });
            toast.success('Solde supprime');
            fetchClient();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const TYPE_PAIEMENT_ACOMPTE = ['Cheque AO', 'Cheque SO', 'Virement', 'Espece'];

    const fetchClient = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true });
            setClient(res.data);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchClient(); }, [id]);

    // Init programmation from client data
    useEffect(() => {
        if (client?.programmation) {
            setProgNbJours(client.programmation.nombre_jours || 0);
            setProgJours(client.programmation.jours || []);
        }
    }, [client]);

    const handleStatutChange = async (newStatut) => {
        setStatutDropOpen(false);
        try {
            await axios.put(`${API_URL}/api/clients/${id}`, { statut: newStatut }, { withCredentials: true });
            toast.success('Statut mis a jour');
            fetchClient();
        } catch {
            toast.error('Erreur');
        }
    };

    const startProgEdit = () => {
        const prog = client?.programmation;
        setProgNbJours(prog?.nombre_jours || 0);
        setProgJours((prog?.jours || []).map(j => ({ ...j })));
        setProgEditMode(true);
    };

    const cancelProgEdit = () => {
        const prog = client?.programmation;
        setProgNbJours(prog?.nombre_jours || 0);
        setProgJours((prog?.jours || []).map(j => ({ ...j })));
        setProgEditMode(false);
    };

    const handleProgNbJoursChange = (delta) => {
        const newVal = Math.max(0, progNbJours + delta);
        setProgNbJours(newVal);
        if (newVal > progJours.length) {
            const added = [];
            for (let i = progJours.length; i < newVal; i++) {
                added.push({ jour_num: i + 1, date: '', heure: '', demenageurs: 0, camion_20m3: 0, camion_30m3: 0, type_prestation: '' });
            }
            setProgJours([...progJours, ...added]);
        } else if (newVal < progJours.length) {
            setProgJours(progJours.slice(0, newVal));
        }
    };

    const updateProgJour = (idx, field, value) => {
        const upd = [...progJours];
        upd[idx] = { ...upd[idx], [field]: value };
        setProgJours(upd);
    };

    const saveProgammation = async () => {
        setProgSaving(true);
        try {
            const r = await resilient.trySave(() => axios.post(`${API_URL}/api/clients/${id}/programmation`, {
                nombre_jours: progNbJours,
                jours: progJours,
            }, { withCredentials: true }));
            if (!r.ok) {
                if (!r.offline) toast.error(r.err?.response?.data?.detail || 'Erreur lors de la sauvegarde');
                return;
            }
            toast.success('Programmation enregistree');
            setProgEditMode(false);
            fetchClient();
        } catch (err) {
            if (!isNetworkError(err)) toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setProgSaving(false);
        }
    };

    // ── Livraison handlers ──
    const saveFournitures = async () => {
        setLivSaving(true);
        try {
            await axios.post(`${API_URL}/api/clients/${id}/livraison`, { fournitures: livFournitures }, { withCredentials: true });
            toast.success('Fournitures enregistrees');
            setLivEditMode(false);
            fetchClient();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setLivSaving(false);
        }
    };

    const handleConfirmLivraison = async () => {
        setLivSaving(true);
        try {
            await axios.post(`${API_URL}/api/clients/${id}/livraison/programmer`, {
                date: livDate, heure: livHeure, remarque: livRemarque,
            }, { withCredentials: true });
            toast.success('Livraison programmee');
            setLivRemarqueModal(false);
            setLivProgMode(false);
            fetchClient();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setLivSaving(false);
        }
    };

    // ── Acompte handlers ──
    const resetAcompteForm = () => {
        setAcompteDate('');
        setAcompteMontant('');
        setAcompteType('');
        setAcompteRemarque('Acompte');
        setAcompteFormOpen(false);
    };

    const handleAddAcompte = async () => {
        if (!acompteDate || !acompteMontant || !acompteType) {
            toast.error('Date, montant et type requis');
            return;
        }
        setAcompteSaving(true);
        try {
            await axios.post(`${API_URL}/api/clients/${id}/acomptes`, {
                date: acompteDate,
                montant: parseFloat(acompteMontant),
                type_paiement: acompteType,
                remarque: acompteRemarque || 'Acompte',
            }, { withCredentials: true });
            toast.success('Acompte enregistre');
            resetAcompteForm();
            fetchClient();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setAcompteSaving(false);
        }
    };

    const handleDeleteAcompte = async (acompteId) => {
        if (!window.confirm('Supprimer cet acompte ?')) return;
        try {
            await axios.delete(`${API_URL}/api/clients/${id}/acomptes/${acompteId}`, { withCredentials: true });
            toast.success('Acompte supprime');
            fetchClient();
        } catch {
            toast.error('Erreur');
        }
    };

    const handleCancelClient = async () => {
        if (!cancelReason.trim()) { toast.error('Veuillez saisir la raison'); return; }
        setCancelSaving(true);
        try {
            await axios.post(`${API_URL}/api/clients/${id}/cancel`, { raison: cancelReason }, { withCredentials: true });
            toast.success('Client annule');
            setCancelDialogOpen(false);
            navigate('/clients');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setCancelSaving(false);
        }
    };

    const handleUncancelClient = async () => {
        try {
            await axios.post(`${API_URL}/api/clients/${id}/uncancel`, {}, { withCredentials: true });
            toast.success('Prestation reactivee');
            fetchClient();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    // ── AOT/MM/GM status change ──
    const handleSectionStatutChange = async (section, type, newStatut) => {
        if (newStatut === 'accorde' || (section === 'aot' && newStatut === 'refuse')) {
            setUploadContext({ section, type, statut: newStatut });
            setUploadFile(null);
            setUploadDialogOpen(true);
            return;
        }
        try {
            // Day-based item: dispatch to /jour/statut endpoint
            if (typeof type === 'string' && type.startsWith('jour_')) {
                const jourIndex = parseInt(type.split('_')[1], 10);
                await axios.post(`${API_URL}/api/clients/${id}/${section}/jour/statut`, { jour_index: jourIndex, statut: newStatut }, { withCredentials: true });
            } else {
                await axios.post(`${API_URL}/api/clients/${id}/${section}/statut`, { type, statut: newStatut }, { withCredentials: true });
            }
            toast.success('Statut mis a jour');
            fetchClient();
        } catch {
            toast.error('Erreur');
        }
    };

    const handleUploadAccorde = async () => {
        if (!uploadFile) { toast.error('Photo ou fichier obligatoire'); return; }
        setUploadSaving(true);
        try {
            const reader = new FileReader();
            const base64 = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(uploadFile); });
            const uploadRes = await axios.post(`${API_URL}/api/clients/${id}/livraison/upload`, { file_data: base64, file_name: uploadFile.name }, { withCredentials: true });
            const upCtx = uploadContext;
            if (typeof upCtx.type === 'string' && upCtx.type.startsWith('jour_')) {
                const jourIndex = parseInt(upCtx.type.split('_')[1], 10);
                await axios.post(`${API_URL}/api/clients/${id}/${upCtx.section}/jour/statut`, {
                    jour_index: jourIndex, statut: upCtx.statut || 'accorde', justificatif_url: uploadRes.data.url,
                }, { withCredentials: true });
            } else {
                await axios.post(`${API_URL}/api/clients/${id}/${upCtx.section}/statut`, {
                    type: upCtx.type, statut: upCtx.statut || 'accorde', justificatif_url: uploadRes.data.url,
                }, { withCredentials: true });
            }
            toast.success('Statut mis a jour');
            setUploadDialogOpen(false);
            fetchClient();
        } catch {
            toast.error('Erreur');
        } finally {
            setUploadSaving(false);
        }
    };

    const openApercu = () => {
        setMenuOpen(false);
        const df = client?.accepted_devis;
        if (!df) return;
        setEditPrix(String(df.data?.prix_prestation || df.total_ht || 0));
        setEditPrestations((df.data?.prestations || []).map(p => ({ ...p })));
        setEditRemarques(df.data?.remarques || '');
        setEditFields({
            nom: df.data?.nom || client.nom || '',
            telephone: df.data?.telephone || client.telephone || '',
            email: df.data?.email || client.email || '',
            adresse_depart: df.data?.adresse_depart || '',
            adresse_arrivee: df.data?.adresse_arrivee || '',
            date_chargement: df.data?.date_chargement || '',
            date_dechargement: df.data?.date_dechargement || '',
            etage_depart: df.data?.etage_depart || '',
            etage_arrivee: df.data?.etage_arrivee || '',
            ascenseur_depart: df.data?.ascenseur_depart || '',
            ascenseur_arrivee: df.data?.ascenseur_arrivee || '',
            aot_depart: df.data?.aot_depart || '',
            aot_arrivee: df.data?.aot_arrivee || '',
            monte_meuble_depart: df.data?.monte_meuble_depart || '',
            monte_meuble_arrivee: df.data?.monte_meuble_arrivee || '',
            garde_meuble: df.data?.garde_meuble || 'non',
            garde_meuble_duree: df.data?.garde_meuble_duree || '',
            garde_meuble_mensualite: df.data?.garde_meuble_mensualite || '',
            fournitures: df.data?.fournitures || 'non',
        });
        setEditMode(false);
        setApercuOpen(true);
    };

    const handleDownload = async () => {
        setMenuOpen(false);
        const dfId = client?.devis_formule_id;
        if (!dfId) return;
        try {
            const res = await axios.get(`${API_URL}/api/devis-formules/${dfId}/pdf`, {
                withCredentials: true, responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `devis_${client.accepted_devis?.numero || 'client'}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Erreur lors du telechargement');
        }
    };

    const handleSaveDevis = async () => {
        setSaving(true);
        const dfId = client?.devis_formule_id;
        try {
            const prixHt = parseFloat(editPrix) || 0;
            let presTotal = editPrestations.reduce((s, p) => s + (parseFloat(p.prix) || 0), 0);
            const totalHt = prixHt + presTotal;
            const tva = Math.round(totalHt * 0.20 * 100) / 100;
            const totalTtc = Math.round((totalHt + tva) * 100) / 100;

            const updatedData = {
                ...df.data,
                ...editFields,
                prix_prestation: String(prixHt),
                prestations: editPrestations,
                remarques: editRemarques,
            };

            const res = await axios.put(`${API_URL}/api/devis-formules/${dfId}`, {
                data: updatedData,
                total_ht: totalHt,
                tva: tva,
                total_ttc: totalTtc,
                formule: df.formule,
            }, { withCredentials: true });

            toast.success(`Nouvelle version creee (${res.data.numero})`);
            setEditMode(false);
            setApercuOpen(false);
            fetchClient();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // ============ DAY CHECKBOXES (AOT / Monte-meuble / Garde-meuble) ============
    const KIND_CONFIG = {
        aot: { url: 'aot/jour', label: "L'AOT", title: 'AOT', accent: 'border-emerald-500 bg-emerald-50 text-emerald-700' },
        mm: { url: 'monte-meuble/jour', label: 'Le monte-meuble', title: 'Monte-meuble', accent: 'border-blue-500 bg-blue-50 text-blue-700' },
        gm: { url: 'garde-meuble/jour', label: 'Le garde-meuble', title: 'Garde-meuble', accent: 'border-purple-500 bg-purple-50 text-purple-700' },
    };

    const askToggleDay = (kind, jour, currentlyChecked) => {
        const will = !currentlyChecked;
        const cfg = KIND_CONFIG[kind];
        setDayDialog({
            open: true, kind,
            label: cfg.label,
            date: jour.date,
            jourIndex: jour.jour_num,
            willEnable: will,
            busy: false,
        });
    };

    const confirmToggleDay = async () => {
        const { kind, jourIndex, willEnable, date } = dayDialog;
        const cfg = KIND_CONFIG[kind];
        setDayDialog(d => ({ ...d, busy: true }));
        try {
            await axios.post(`${API_URL}/api/clients/${id}/${cfg.url}`, {
                jour_index: jourIndex, enabled: willEnable,
            }, { withCredentials: true });
            toast.success(willEnable
                ? `${cfg.title} planifié pour le ${formatDate(date)}`
                : `${cfg.title} retiré du ${formatDate(date)}`);
            setDayDialog({ open: false, kind: '', label: '', date: '', jourIndex: 0, willEnable: true, busy: false });
            fetchClient();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
            setDayDialog(d => ({ ...d, busy: false }));
        }
    };

    const renderDayChips = (kind, opts = {}) => {
        const days = client.prog_days || [];
        if (!days.length) return null;
        const stateMap = kind === 'aot' ? (client.aot_jours || {})
            : kind === 'mm' ? (client.monte_meuble_jours || {})
                : (client.garde_meuble_jours || {});
        const cfg = KIND_CONFIG[kind];
        const showLabel = opts.showLabel !== false;
        return (
            <div className="flex flex-wrap items-center gap-2" data-testid={`day-chips-${kind}`}>
                {showLabel && (
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Jours :</span>
                )}
                {days.map((j) => {
                    const state = stateMap[String(j.jour_num)] || stateMap[j.jour_num];
                    const checked = !!(state && state.statut !== 'non_planifie');
                    return (
                        <button
                            key={j.jour_num}
                            type="button"
                            onClick={() => askToggleDay(kind, j, checked)}
                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                                checked
                                    ? cfg.accent + ' shadow-sm'
                                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                            }`}
                            data-testid={`day-chip-${kind}-${j.jour_num}`}
                        >
                            <span className={`w-3.5 h-3.5 rounded-[3px] flex items-center justify-center border ${checked ? 'bg-current border-current' : 'border-gray-300'}`}>
                                {checked && (
                                    <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                                        <path d="M2 6.5l2.5 2.5L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span className="font-bold">J{j.jour_num}</span>
                            <span className="opacity-80">→ {formatDate(j.date)}</span>
                        </button>
                    );
                })}
            </div>
        );
    };

    // Bloc affectation par jour à afficher dans la section "Programmation de prestation"
    const renderProgAffectationsBlock = () => {
        const days = client?.prog_days || [];
        if (!days.length) return null;
        const ROWS = [
            { kind: 'aot', label: 'AOT', dot: 'bg-emerald-500' },
            { kind: 'mm', label: 'Monte-meuble', dot: 'bg-blue-500' },
            { kind: 'gm', label: 'Garde-meuble', dot: 'bg-purple-500' },
        ];
        return (
            <div className="mt-5 pt-5 border-t border-dashed border-gray-200" data-testid="prog-affectations-block">
                <h3 className="text-xs font-bold text-[#1B365D] uppercase tracking-widest mb-3">Affectations par jour</h3>
                <div className="space-y-2.5">
                    {ROWS.map(r => (
                        <div key={r.kind} className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-2 min-w-[140px] shrink-0">
                                <span className={`w-2 h-2 rounded-full ${r.dot}`} />
                                <span className="text-sm font-semibold text-gray-700">{r.label}</span>
                            </div>
                            {renderDayChips(r.kind, { showLabel: false })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B365D]" /></div></Layout>;
    if (!client) return <Layout><div className="text-center py-20 text-gray-400">Client non trouve</div></Layout>;

    // Garde-meuble actif = au moins une entrée sans date_sortie dépassée
    const today = new Date().toISOString().slice(0, 10);
    const gmActif = (client.garde_meuble || []).some(item => !item.date_sortie || item.date_sortie >= today);

    const sCfg = STATUT_CONFIG[client.statut] || STATUT_CONFIG.en_attente;
    const df = client.accepted_devis;
    const formuleLabel = FORMULE_LABELS[client.formule] || client.formule || '-';

    const formatDate = (s) => {
        if (!s) return '-';
        try {
            const d = new Date(typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s + 'T00:00:00' : s);
            if (isNaN(d.getTime())) return s;
            return d.toLocaleDateString('fr-FR');
        } catch { return s; }
    };

    // Build effective AOT/MM/GM items from checked-day toggles. Falls back to legacy items when none.
    const buildEffectiveItems = (kind) => {
        const days = client?.prog_days || [];
        const stateMap = kind === 'aot' ? (client?.aot_jours || {})
            : kind === 'mm' ? (client?.monte_meuble_jours || {})
                : (client?.garde_meuble_jours || {});
        const checked = days
            .map(j => {
                const st = stateMap[String(j.jour_num)] || stateMap[j.jour_num];
                if (!st || st.statut === 'non_planifie') return null;
                return { jour_num: j.jour_num, date: j.date, statut: st.statut, justificatif_url: st.justificatif_url || '' };
            })
            .filter(Boolean);
        if (checked.length > 0) {
            const fallbackAddr = (client?.adresse_arrivee || client?.adresse_depart
                || (client?.aot && client.aot[0]?.adresse) || '');
            return checked.map(c => {
                const baseGm = (client?.garde_meuble && client.garde_meuble[0]) || {};
                return {
                    type: `jour_${c.jour_num}`,
                    jour_num: c.jour_num,
                    adresse: fallbackAddr,
                    // generic
                    date: c.date,
                    // garde-meuble specific aliases (rendered as Entree / Sortie / Tarif / Duree)
                    date_entree: c.date,
                    date_sortie: client?.date_sortie_gm_auto || baseGm.date_sortie || '',
                    adresse_gm: baseGm.adresse_gm || fallbackAddr,
                    tarif_mensuel: baseGm.tarif_mensuel || 0,
                    duree: baseGm.duree || '',
                    statut: c.statut,
                    justificatif_url: c.justificatif_url,
                    _isDay: true,
                };
            });
        }
        if (kind === 'aot') return client?.aot || [];
        if (kind === 'mm') return client?.monte_meuble || [];
        return client?.garde_meuble || [];
    };

    // Computed totals for apercu
    const computedPrixHt = parseFloat(editPrix) || 0;
    const computedPresTotal = editPrestations.reduce((s, p) => s + (parseFloat(p.prix) || 0), 0);
    const computedTotalHt = computedPrixHt + computedPresTotal;
    const computedTva = Math.round(computedTotalHt * 0.20 * 100) / 100;
    const computedTotalTtc = Math.round((computedTotalHt + computedTva) * 100) / 100;

    const effectiveAot = buildEffectiveItems('aot');
    const effectiveMm = buildEffectiveItems('mm');
    const effectiveGm = buildEffectiveItems('gm');

    return (
        <Layout>
            {resilient.Modal}
            <div className="space-y-6 max-w-5xl mx-auto" data-testid="client-detail-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/clients')} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm" data-testid="back-btn">
                            <ArrowLeft className="w-4 h-4" /> Retour
                        </button>
                        <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Client Apercu —{client.nom}
                        </h1>
                        <ImportantNotesButton
                            prospectId={client.prospect_id}
                            prospectName={client.nom}
                            variant="badge"
                        />
                    </div>
                </div>

                {/* Important Notes Banner */}
                <ImportantNotesBanner prospectId={client.prospect_id} />

                {/* Cancelled banner — visible only when the client is cancelled */}
                {client.cancelled && (
                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 flex items-start gap-3" data-testid="client-cancelled-banner">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                            <XCircle className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-bold text-red-700">Prestation annulée</h2>
                            {client.cancel_raison && (
                                <p className="text-sm text-red-700 italic mt-1 whitespace-pre-wrap break-words">« {client.cancel_raison} »</p>
                            )}
                            {client.cancelled_by && (
                                <p className="text-[11px] text-red-600/80 mt-1.5">
                                    Annulée {client.cancelled_at ? `le ${new Date(client.cancelled_at).toLocaleDateString('fr-FR')}` : ''} par {client.cancelled_by}
                                </p>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-2 shrink-0"
                            onClick={handleUncancelClient}
                            data-testid="uncancel-client-btn"
                            disabled={!canDeleteClient}
                            title={!canDeleteClient ? 'Accès refusé : permission requise' : 'Réactiver la prestation'}
                        >
                            <CheckCircle2 className="w-4 h-4" /> Réactiver
                        </Button>
                    </div>
                )}

                {/* Section 1: Statut du client */}
                <div className="bg-blue-50 rounded-xl border-2 border-amber-300 p-5" data-testid="section-statut-client">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-[#1B365D]" />
                            <h2 className="text-base font-bold text-[#1B365D]">1. Statut du client</h2>
                        </div>
                        {!client.cancelled && (
                            <Button variant="outline" className="border-red-300 text-red-500 hover:bg-red-50 hover:text-red-600 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!canDeleteClient}
                                title={!canDeleteClient ? 'Accès refusé : permission Suppression requise' : 'Annuler'}
                                onClick={() => setCancelDialogOpen(true)} data-testid="cancel-client-btn">
                                <XCircle className="w-4 h-4" /> Annulee
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold bg-white shadow-sm min-w-[180px]" data-testid="client-statut-display">
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                            <span className="text-[#1B365D]">{sCfg.label}</span>
                        </div>
                        <Badge className={`${sCfg.color} border text-sm px-3 py-1.5 font-semibold`}>{sCfg.label}</Badge>
                    </div>
                </div>

                {/* Section 2: Contacter le client */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="section-contact-client">
                    <div className="flex items-center gap-2 mb-5">
                        <Users className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-base font-bold text-[#1B365D]">2. Contacter le client</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-6 mb-5">
                        <div>
                            <p className="text-xs text-red-400 font-medium mb-1">Nom</p>
                            <p className="text-sm font-semibold text-gray-800">{client.nom || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-red-400 font-medium mb-1">Telephone</p>
                            <p className="text-sm font-semibold text-gray-800">{client.telephone || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-red-400 font-medium mb-1">E-mail</p>
                            <p className="text-sm font-semibold text-gray-800">{client.email || '-'}</p>
                        </div>
                    </div>
                    <div className="mb-6">
                        <p className="text-xs text-gray-400 font-medium mb-1">Commercial</p>
                        <p className="text-sm text-gray-400 italic">{client.commercial || 'Non attribue'}</p>
                    </div>
                    <div className="flex items-stretch gap-0 rounded-xl border border-gray-200 overflow-hidden">
                        <div className="flex-1 p-4 bg-white">
                            <div className="flex items-center gap-1 mb-2">
                                <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Partir</span>
                            </div>
                            <p className="text-sm text-gray-700">{client.adresse_depart || '-'}</p>
                        </div>
                        <div className="flex items-center px-3">
                            <div className="flex items-center gap-0.5">
                                <div className="w-2 h-0.5 bg-gray-300 rounded" />
                                <div className="w-2 h-0.5 bg-gray-300 rounded" />
                                <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[8px] border-l-blue-400" />
                            </div>
                        </div>
                        <div className="flex-1 p-4 bg-white border-l border-gray-200">
                            <div className="flex items-center gap-1 mb-2">
                                <MapPin className="w-3.5 h-3.5 text-red-400" />
                                <span className="text-xs font-bold text-red-500 uppercase tracking-wide">Arriver</span>
                            </div>
                            <p className="text-sm text-gray-700">{client.adresse_arrivee || '-'}</p>
                        </div>
                    </div>
                </div>

                {/* Section 3: Devis accepte */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="section-devis-accepte">
                    <div className="flex items-center gap-2 mb-5">
                        <FileText className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-base font-bold text-[#1B365D]">3. Devis accepte — {formuleLabel}</h2>
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs">Accepter</Badge>
                    </div>

                    {df ? (
                        <div className="border-2 border-emerald-300 rounded-xl p-5 bg-emerald-50/30">
                            <div className="flex items-center gap-1 mb-4">
                                <Clock className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-xs font-bold text-emerald-600">Les devis acceptent</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="grid grid-cols-4 gap-8 flex-1">
                                    <div>
                                        <p className="text-xs text-red-400 font-medium mb-1">N Devis</p>
                                        <p className="text-sm font-bold text-gray-800">{df.numero}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium mb-1">Cree le</p>
                                        <p className="text-sm text-gray-700">{formatDate(df.created_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium mb-1">Formule</p>
                                        <Badge className="text-xs bg-blue-50 text-[#1B365D] border border-blue-200">
                                            {FORMULE_LABELS[df.formule] || df.formule}
                                        </Badge>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium mb-1">Prix</p>
                                        <p className="text-lg font-bold text-gray-800">{df.total_ttc?.toFixed(2)} EUR</p>
                                    </div>
                                </div>
                                {/* Menu "..." */}
                                <div className="relative">
                                    <button className="p-2 rounded-lg hover:bg-white/80 text-gray-400 hover:text-gray-600 transition-colors"
                                        onClick={() => setMenuOpen(!menuOpen)} data-testid="devis-menu-btn">
                                        <MoreHorizontal className="w-5 h-5" />
                                    </button>
                                    {menuOpen && (
                                        <>
                                            <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                                            <div className="absolute right-0 bottom-full mb-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 w-48" data-testid="devis-context-menu">
                                                <button onClick={openApercu}
                                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                    <Eye className="w-4 h-4 text-gray-400" /> Apercu
                                                </button>
                                                <button onClick={handleDownload}
                                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                                    <Download className="w-4 h-4 text-gray-400" /> Telecharger
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-400 text-sm">Aucun devis accepte</div>
                    )}
                </div>

                {/* Section 4: Programmation de la prestation */}
                <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6" data-testid="section-programmation">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4 text-[#1B365D]" />
                            <h2 className="text-base font-bold text-[#1B365D]">4. Programmation de la prestation</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            {progEditMode ? (
                                <>
                                    <Button variant="outline" onClick={cancelProgEdit} data-testid="prog-cancel-btn">Annuler</Button>
                                    <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={saveProgammation} disabled={progSaving} data-testid="prog-save-btn">
                                        {progSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                        Enregistrer
                                    </Button>
                                </>
                            ) : (
                                <Button variant="outline" onClick={startProgEdit} data-testid="prog-modifier-btn">Modifier</Button>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-gray-100 pt-5">
                        {progEditMode ? (
                            <div className="space-y-6">
                                {/* Nombre de jours control */}
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-gray-600 font-medium">Nombre de jours :</span>
                                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                        <button onClick={() => handleProgNbJoursChange(-1)}
                                            className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 text-lg font-bold transition-colors border-r border-gray-200"
                                            data-testid="prog-minus-btn">
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="px-5 py-2 text-center text-sm font-bold text-gray-800 min-w-[40px]" data-testid="prog-nb-jours">{progNbJours}</span>
                                        <button onClick={() => handleProgNbJoursChange(1)}
                                            className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 text-lg font-bold transition-colors border-l border-gray-200"
                                            data-testid="prog-plus-btn">
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {progNbJours === 0 && (
                                    <p className="text-center text-gray-400 italic text-sm py-6">Saisissez le nombre de jours pour programmer la prestation</p>
                                )}

                                {/* Day blocks */}
                                {progJours.slice(0, progNbJours).map((jour, idx) => (
                                    <div key={idx} className="border border-gray-200 rounded-xl p-5 bg-gray-50/30" data-testid={`prog-jour-${idx + 1}`}>
                                        <h3 className="text-sm font-bold text-blue-600 mb-4">Jour {idx + 1}</h3>
                                        <div className="grid grid-cols-6 gap-4">
                                            <div>
                                                <label className="text-xs text-red-400 font-medium mb-1 block">Date <span className="text-red-500">*</span></label>
                                                <Input type="date" value={jour.date} onChange={e => updateProgJour(idx, 'date', e.target.value)}
                                                    className="text-sm" data-testid={`prog-jour-${idx + 1}-date`} />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Heure <span className="text-red-500">*</span></label>
                                                <Input type="time" value={jour.heure} onChange={e => updateProgJour(idx, 'heure', e.target.value)}
                                                    className="text-sm" data-testid={`prog-jour-${idx + 1}-heure`} />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Demenageurs <span className="text-red-500">*</span></label>
                                                <Input type="number" min="0" value={jour.demenageurs} onChange={e => updateProgJour(idx, 'demenageurs', parseInt(e.target.value) || 0)}
                                                    className="text-sm" data-testid={`prog-jour-${idx + 1}-demenageurs`} />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Camion 20m3 <span className="text-red-500">*</span></label>
                                                <Input type="number" min="0" value={jour.camion_20m3} onChange={e => updateProgJour(idx, 'camion_20m3', parseInt(e.target.value) || 0)}
                                                    className="text-sm" data-testid={`prog-jour-${idx + 1}-camion20`} />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Camion 30m3 <span className="text-red-500">*</span></label>
                                                <Input type="number" min="0" value={jour.camion_30m3} onChange={e => updateProgJour(idx, 'camion_30m3', parseInt(e.target.value) || 0)}
                                                    className="text-sm" data-testid={`prog-jour-${idx + 1}-camion30`} />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Type <span className="text-red-500">*</span></label>
                                                <select value={jour.type_prestation} onChange={e => updateProgJour(idx, 'type_prestation', e.target.value)}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                                                    data-testid={`prog-jour-${idx + 1}-type`}>
                                                    <option value="">Choisir</option>
                                                    {TYPE_PRESTATION_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                {(client?.programmation?.nombre_jours || 0) === 0 ? (
                                    <p className="text-center text-gray-400 italic text-sm py-10">Aucune programmation</p>
                                ) : (
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-500 font-medium">{client.programmation.nombre_jours} jour(s) programme(s)</p>
                                        {(client.programmation.jours || []).map((jour, idx) => (
                                            <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50/30" data-testid={`prog-view-jour-${idx + 1}`}>
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-sm font-bold text-blue-600">Jour {idx + 1}</h3>
                                                    {jour.event_id && (
                                                        <button
                                                            type="button"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                const reqUrl = `${API_URL}/api/agenda-demenagement/event/${jour.event_id}/lettre-voiture-pdf`;
                                                                toast.loading('Génération LV...', { id: `lv-${jour.event_id}` });
                                                                try {
                                                                    const res = await fetch(reqUrl, { credentials: 'include' });
                                                                    if (!res.ok) {
                                                                        let detail = `Erreur ${res.status}`;
                                                                        try {
                                                                            const j = await res.json();
                                                                            detail = j.detail || j.message || detail;
                                                                        } catch { /* keep */ }
                                                                        console.error('LV server error', res.status, detail);
                                                                        toast.error(detail, { id: `lv-${jour.event_id}`, duration: 10000 });
                                                                        return;
                                                                    }
                                                                    const blob = await res.blob();
                                                                    const url = URL.createObjectURL(blob);
                                                                    const nom = (client.nom || 'client').replace(/\s+/g, '_');
                                                                    const filename = `LV_${nom}_${jour.date || ''}.pdf`;
                                                                    const a = document.createElement('a');
                                                                    a.href = url;
                                                                    a.download = filename;
                                                                    a.rel = 'noopener';
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    a.remove();
                                                                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                                                                    if (isIOS) {
                                                                        setTimeout(() => { try { window.open(url, '_blank'); } catch { /* no-op */ } }, 100);
                                                                    }
                                                                    setTimeout(() => URL.revokeObjectURL(url), 4000);
                                                                    toast.success('Lettre de voiture téléchargée', { id: `lv-${jour.event_id}` });
                                                                } catch (err) {
                                                                    console.error('LV fetch failed', err);
                                                                    toast.error(`Erreur réseau : ${err?.message || 'inconnue'}`, { id: `lv-${jour.event_id}`, duration: 10000 });
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md border border-[#1B365D] text-[#1B365D] hover:bg-[#1B365D] hover:text-white transition-colors"
                                                            data-testid={`client-lv-btn-${idx + 1}`}
                                                            title="Télécharger la lettre de voiture pour ce jour"
                                                        >
                                                            <FileText className="w-3 h-3" />
                                                            LV jour {idx + 1}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-6 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-400 text-xs">Date</span>
                                                        <p className="text-gray-700 font-medium">{jour.date ? new Date(jour.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-xs">Heure</span>
                                                        <p className="text-gray-700 font-medium">{jour.heure || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-xs">Demenageurs</span>
                                                        <p className="text-gray-700 font-medium">{jour.demenageurs || 0}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-xs">Camion 20m3</span>
                                                        <p className="text-gray-700 font-medium">{jour.camion_20m3 || 0}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-xs">Camion 30m3</span>
                                                        <p className="text-gray-700 font-medium">{jour.camion_30m3 || 0}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-xs">Type</span>
                                                        <p className="text-gray-700 font-medium">{jour.type_prestation || '-'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {renderProgAffectationsBlock()}
                </div>

                {/* Section 5: Livraison de la programmation */}
                <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6" data-testid="section-livraison">
                    <div className="flex items-center gap-2 mb-5">
                        <Package className="w-4 h-4 text-[#1B365D]" />
                        <h2 className="text-base font-bold text-[#1B365D]">5. Livraison de la programmation</h2>
                    </div>
                    <div className="border-t border-gray-100 pt-5">
                        {!client.livraison_data && !livEditMode ? (
                            /* Case 1: No fournitures */
                            <div className="border border-gray-200 rounded-xl p-6 bg-gray-50/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-2">Fournitures : <strong>Non</strong></p>
                                        <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-xs" data-testid="badge-sans-livraison">Sans livraison</Badge>
                                    </div>
                                    <Button variant="outline" className="gap-2" onClick={() => setLivEditMode(true)} data-testid="ajouter-livraison-btn">
                                        <Plus className="w-4 h-4" /> Ajouter une livraison
                                    </Button>
                                </div>
                            </div>
                        ) : livEditMode ? (
                            /* Edit fournitures form */
                            <div className="space-y-5">
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">Detail des fournitures</h3>
                                    <div className="grid grid-cols-4 gap-4">
                                        {[
                                            ['cartons_standards', 'Cartons standards'],
                                            ['cartons_livres', 'Cartons livres'],
                                            ['adhesif', 'Adhesif'],
                                            ['papier_bulle', 'Papier bulle'],
                                        ].map(([key, label]) => (
                                            <div key={key} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between bg-white">
                                                <span className="text-sm text-gray-600">{label}</span>
                                                <Input type="number" min="0" value={livFournitures[key]}
                                                    onChange={e => setLivFournitures({ ...livFournitures, [key]: parseInt(e.target.value) || 0 })}
                                                    className="w-16 text-right font-bold text-sm" data-testid={`liv-${key}`} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={() => setLivEditMode(false)}>Annuler</Button>
                                    <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={saveFournitures} disabled={livSaving} data-testid="liv-save-btn">
                                        {livSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                        Enregistrer
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* Case 2 & 3: Fournitures present */
                            <div className="space-y-5">
                                {/* Fournitures detail */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">Detail des fournitures</h3>
                                    <div className="grid grid-cols-4 gap-4">
                                        {[
                                            ['cartons_standards', 'Cartons standards'],
                                            ['cartons_livres', 'Cartons livres'],
                                            ['adhesif', 'Adhesif'],
                                            ['papier_bulle', 'Papier bulle'],
                                        ].map(([key, label]) => (
                                            <div key={key} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between bg-white" data-testid={`liv-view-${key}`}>
                                                <span className="text-sm text-gray-600">{label}</span>
                                                <span className="text-sm font-bold text-gray-800">{client.livraison_data?.fournitures?.[key] || 0}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Status + Action */}
                                {client.livraison_data?.statut === 'programme' || client.livraison_data?.statut === 'livre' || client.livraison_data?.statut === 'en_attente_paiement' ? (
                                    <div className="border-2 border-emerald-200 rounded-xl p-5 bg-emerald-50/30">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs mb-2" data-testid="badge-programme">
                                                    {client.livraison_data.statut === 'livre' ? 'Livree' : client.livraison_data.statut === 'en_attente_paiement' ? 'En attente paiement' : 'Programmee'}
                                                </Badge>
                                                <p className="text-sm text-gray-600">
                                                    {client.livraison_data.date_livraison ? new Date(client.livraison_data.date_livraison + 'T00:00').toLocaleDateString('fr-FR') : ''} {client.livraison_data.heure_livraison || ''}
                                                </p>
                                                {client.livraison_data.remarque && (
                                                    <p className="text-xs text-gray-400 mt-1">Remarque : {client.livraison_data.remarque}</p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {client.livraison_data.statut === 'programme' && (
                                                    <Button variant="outline" className="gap-2" onClick={() => {
                                                        setLivDate(client.livraison_data.date_livraison || '');
                                                        setLivHeure(client.livraison_data.heure_livraison || '');
                                                        setLivProgMode(true);
                                                    }} data-testid="reprogrammer-livraison-btn">
                                                        <CalendarDays className="w-4 h-4" /> Reprogrammer
                                                    </Button>
                                                )}
                                                <Button variant="outline" onClick={() => {
                                                    setLivFournitures(client.livraison_data.fournitures || { cartons_standards: 0, cartons_livres: 0, adhesif: 0, papier_bulle: 0 });
                                                    setLivEditMode(true);
                                                }}>Modifier fournitures</Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border-2 border-amber-200 rounded-xl p-5 bg-amber-50/30">
                                        <div className="flex items-center justify-between">
                                            <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-xs" data-testid="badge-a-programmer">A programmer</Badge>
                                            {!livProgMode ? (
                                                <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => setLivProgMode(true)} data-testid="programmer-livraison-btn">
                                                    <CalendarDays className="w-4 h-4" /> Programmer une livraison
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>
                                )}

                                {/* Programming form (for new or reprogramming) */}
                                {livProgMode && (
                                    <div className="border-2 border-blue-200 rounded-xl p-5" data-testid="liv-prog-form">
                                        <h3 className="text-sm font-bold text-gray-800 mb-4">Programmer la livraison</h3>
                                        <div className="flex items-end gap-4">
                                            <div>
                                                <label className="text-xs text-red-400 font-medium mb-1 block">Date</label>
                                                <Input type="date" value={livDate} onChange={e => setLivDate(e.target.value)} data-testid="liv-date" />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Heure</label>
                                                <Input type="time" value={livHeure} onChange={e => setLivHeure(e.target.value)} data-testid="liv-heure" />
                                            </div>
                                            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => {
                                                if (!livDate) { toast.error('Date requise'); return; }
                                                setLivRemarqueModal(true);
                                            }} data-testid="liv-confirmer-btn">
                                                <CheckCircle2 className="w-4 h-4" /> Confirmer
                                            </Button>
                                            <Button variant="outline" onClick={() => setLivProgMode(false)}>Annuler</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 6: Acompte */}
                <div className="bg-white rounded-xl border-2 border-amber-300 shadow-sm p-6" data-testid="section-acompte">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                            <Euro className="w-4 h-4 text-blue-600" />
                            <h2 className="text-base font-bold text-[#1B365D]">6. Compte</h2>
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-xs">Un regulateur</Badge>
                        </div>
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={() => setAcompteFormOpen(true)} data-testid="ajouter-acompte-btn">
                            <Plus className="w-4 h-4" /> Ajouter un compte
                        </Button>
                    </div>

                    <div className="border-t border-gray-100 pt-5">
                        {/* Add form */}
                        {acompteFormOpen && (
                            <div className="border-2 border-blue-200 rounded-xl p-5 mb-5" data-testid="acompte-form">
                                <h3 className="text-sm font-bold text-gray-800 mb-4">Nouveau compte</h3>
                                <div className="grid grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium mb-1 block">Date du paiement</label>
                                        <Input type="date" value={acompteDate} onChange={e => setAcompteDate(e.target.value)}
                                            data-testid="acompte-date" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium mb-1 block">Montant (EUR)</label>
                                        <Input type="number" min="0" step="0.01" value={acompteMontant} onChange={e => setAcompteMontant(e.target.value)}
                                            placeholder="0,00" data-testid="acompte-montant" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium mb-1 block">Type de paiement</label>
                                        <select value={acompteType} onChange={e => setAcompteType(e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                                            data-testid="acompte-type">
                                            <option value="">Choisir</option>
                                            {TYPE_PAIEMENT_ACOMPTE.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium mb-1 block">Remarque</label>
                                        <Input value={acompteRemarque} onChange={e => setAcompteRemarque(e.target.value)}
                                            placeholder="Acompte" data-testid="acompte-remarque" />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={resetAcompteForm}>Annuler</Button>
                                    <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleAddAcompte} disabled={acompteSaving} data-testid="acompte-confirm-btn">
                                        {acompteSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                        Confirmer
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* List of acomptes */}
                        {(!client.acomptes || client.acomptes.length === 0) && !acompteFormOpen ? (
                            <p className="text-center text-gray-400 italic text-sm py-10">Aucun compte enregistre</p>
                        ) : client.acomptes?.length > 0 && (
                            <div className="space-y-3">
                                {client.acomptes.map((ac) => (
                                    <div key={ac.id} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between bg-white hover:border-gray-300 transition-colors" data-testid={`acompte-row-${ac.id}`}>
                                        <div className="grid grid-cols-4 gap-6 flex-1">
                                            <div>
                                                <span className="text-xs text-gray-400">Date</span>
                                                <p className="text-sm font-medium text-gray-700">{ac.date ? new Date(ac.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400">Montant</span>
                                                <p className="text-sm font-bold text-gray-800">{ac.montant?.toFixed(2)} EUR</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400">Type</span>
                                                <p className="text-sm font-medium text-gray-700">{ac.type_paiement}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400">Remarque</span>
                                                <p className="text-sm text-gray-600">{ac.remarque || '-'}</p>
                                            </div>
                                        </div>
                                        <button onClick={() => handleDeleteAcompte(ac.id)}
                                            className="p-2 text-gray-300 hover:text-red-400 transition-colors ml-2" data-testid={`delete-acompte-${ac.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                <div className="border-t border-dashed border-gray-200 pt-3 mt-3 flex justify-end">
                                    <div className="text-sm">
                                        <span className="text-gray-500">Total acomptes : </span>
                                        <span className="font-bold text-[#1B365D]">{client.acomptes.reduce((s, a) => s + (a.montant || 0), 0).toFixed(2)} EUR</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 7: AOT */}
                <div className="bg-white rounded-xl border-2 border-emerald-300 shadow-sm p-6" data-testid="section-aot">
                    <div className="flex items-center gap-2 mb-5">
                        <Shield className="w-4 h-4 text-blue-600" />
                        <h2 className="text-base font-bold text-[#1B365D]">7. AOT</h2>
                        {(client.aot?.length > 0 || effectiveAot.length > 0) && (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs px-2">{effectiveAot.length || client.aot.length}</Badge>
                        )}
                    </div>
                    <div className="border-t border-gray-100 pt-5">
                        {(effectiveAot.length === 0) ? (
                            <p className="text-center text-gray-400 italic text-sm py-8">Aucune AOT</p>
                        ) : (
                            <div className="space-y-3">
                                {effectiveAot.map((item, idx) => {
                                    const currentStatut = item.statut || 'en_attente';
                                    const sCfg = AOT_STATUT_OPTIONS.find(o => o.value === currentStatut) || AOT_STATUT_OPTIONS[0];
                                    const dropKey = `aot-${item.type}`;
                                    return (
                                    <div key={idx} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`aot-${item.type}`}>
                                        <div className="flex items-center gap-3">
                                            {item._isDay ? (
                                                <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200 border">
                                                    J{item.jour_num}
                                                </Badge>
                                            ) : (
                                                <Badge className={`text-xs ${item.type === 'depart' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-600 border-red-200'} border`}>
                                                    {item.type === 'depart' ? 'Partir' : 'Arriver'}
                                                </Badge>
                                            )}
                                            <div>
                                                <p className="text-sm font-medium text-gray-800">{item.adresse || '-'}</p>
                                                <p className="text-xs text-gray-400">{item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                            </div>
                                        </div>
                                        <div className="relative" data-testid={`aot-statut-${item.type}`}>
                                            <button onClick={() => setOpenStatutDrop(openStatutDrop === dropKey ? null : dropKey)}
                                                className={`flex items-center gap-2 ${sCfg.bg} ${sCfg.border} border rounded-xl px-3.5 py-2 text-xs font-semibold shadow-sm hover:shadow-md transition-all min-w-[140px]`}>
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                                                <span className={`${sCfg.text} flex-1 text-left`}>{sCfg.label}</span>
                                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${openStatutDrop === dropKey ? 'rotate-180' : ''}`} />
                                            </button>
                                            {openStatutDrop === dropKey && (
                                                <>
                                                    <div className="fixed inset-0 z-30" onClick={() => setOpenStatutDrop(null)} />
                                                    <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[170px]">
                                                        {AOT_STATUT_OPTIONS.map(o => (
                                                            <button key={o.value} onClick={() => { setOpenStatutDrop(null); handleSectionStatutChange('aot', item.type, o.value); }}
                                                                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-3 transition-colors ${currentStatut === o.value ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.dot}`} />
                                                                <span className="text-gray-700 flex-1">{o.label}</span>
                                                                {currentStatut === o.value && <span className="text-[#1B365D]">&#10003;</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {item.justificatif_url && currentStatut === 'accorde' && (
                                            <a href={`${API_URL}${item.justificatif_url}`} target="_blank" rel="noreferrer"
                                                className="text-xs text-blue-500 hover:text-blue-700 underline ml-3 flex items-center gap-1" data-testid={`aot-justif-${item.type}`}>
                                                <Download className="w-3 h-3" /> Justificatif
                                            </a>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 8: Monte-meuble */}
                <div className="bg-white rounded-xl border-2 border-emerald-300 shadow-sm p-6" data-testid="section-monte-meuble">
                    <div className="flex items-center gap-2 mb-5">
                        <ArrowUpDown className="w-4 h-4 text-blue-600" />
                        <h2 className="text-base font-bold text-[#1B365D]">8. Monte-meuble</h2>
                        {(client.monte_meuble?.length > 0 || effectiveMm.length > 0) && (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs px-2">{effectiveMm.length || client.monte_meuble.length}</Badge>
                        )}
                    </div>
                    <div className="border-t border-gray-100 pt-5">
                        {(effectiveMm.length === 0) ? (
                            <p className="text-center text-gray-400 italic text-sm py-8">Aucun monte-meuble</p>
                        ) : (
                            <div className="space-y-3">
                                {effectiveMm.map((item, idx) => {
                                    const currentStatut = item.statut || 'en_attente';
                                    const sCfg = MM_STATUT_OPTIONS.find(o => o.value === currentStatut) || MM_STATUT_OPTIONS[0];
                                    const dropKey = `mm-${item.type}`;
                                    return (
                                    <div key={idx} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`mm-${item.type}`}>
                                        <div className="flex items-center gap-3">
                                            {item._isDay ? (
                                                <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200 border">
                                                    J{item.jour_num}
                                                </Badge>
                                            ) : (
                                                <Badge className={`text-xs ${item.type === 'depart' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-600 border-red-200'} border`}>
                                                    {item.type === 'depart' ? 'Partir' : 'Arriver'}
                                                </Badge>
                                            )}
                                            <div>
                                                <p className="text-sm font-medium text-gray-800">{item.adresse || '-'}</p>
                                                <p className="text-xs text-gray-400">{item.date ? new Date(item.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                            </div>
                                        </div>
                                        <div className="relative" data-testid={`mm-statut-${item.type}`}>
                                            <button onClick={() => setOpenStatutDrop(openStatutDrop === dropKey ? null : dropKey)}
                                                className={`flex items-center gap-2 ${sCfg.bg} ${sCfg.border} border rounded-xl px-3.5 py-2 text-xs font-semibold shadow-sm hover:shadow-md transition-all min-w-[140px]`}>
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                                                <span className={`${sCfg.text} flex-1 text-left`}>{sCfg.label}</span>
                                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${openStatutDrop === dropKey ? 'rotate-180' : ''}`} />
                                            </button>
                                            {openStatutDrop === dropKey && (
                                                <>
                                                    <div className="fixed inset-0 z-30" onClick={() => setOpenStatutDrop(null)} />
                                                    <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[170px]">
                                                        {MM_STATUT_OPTIONS.map(o => (
                                                            <button key={o.value} onClick={() => { setOpenStatutDrop(null); handleSectionStatutChange('monte-meuble', item.type, o.value); }}
                                                                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-3 transition-colors ${currentStatut === o.value ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.dot}`} />
                                                                <span className="text-gray-700 flex-1">{o.label}</span>
                                                                {currentStatut === o.value && <span className="text-[#1B365D]">&#10003;</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 9: Garde-meuble */}
                <div className="bg-white rounded-xl border-2 border-emerald-300 shadow-sm p-6" data-testid="section-garde-meuble">
                    <div className="flex items-center gap-2 mb-5">
                        <Warehouse className="w-4 h-4 text-blue-600" />
                        <h2 className="text-base font-bold text-[#1B365D]">9. Garde-meuble</h2>
                        {(client.garde_meuble?.length > 0 || effectiveGm.length > 0) && (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200 text-xs px-2">{effectiveGm.length || client.garde_meuble.length}</Badge>
                        )}
                    </div>
                    <div className="border-t border-gray-100 pt-5">
                        {(effectiveGm.length === 0) ? (
                            <p className="text-center text-gray-400 italic text-sm py-8">Aucun garde-meuble</p>
                        ) : (
                            <div className="space-y-3">
                                {effectiveGm.map((item, idx) => {
                                    const currentStatut = item.statut || 'en_attente';
                                    const sCfg = GM_STATUT_OPTIONS.find(o => o.value === currentStatut) || GM_STATUT_OPTIONS[0];
                                    const dropKey = `gm-${idx}`;
                                    return (
                                    <div key={idx} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`gm-${idx}`}>
                                        <div>
                                            <p className="text-sm font-bold text-gray-800">Entree : {item.date_entree ? new Date(item.date_entree + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                            <p className="text-xs text-gray-400">
                                                Tarif : {item.tarif_mensuel || 0}EUR/mois &nbsp;&nbsp; Duree : {item.duree || '-'}
                                            </p>
                                        </div>
                                        <div className="relative" data-testid={`gm-statut-${idx}`}>
                                            <button onClick={() => setOpenStatutDrop(openStatutDrop === dropKey ? null : dropKey)}
                                                className={`flex items-center gap-2 ${sCfg.bg} ${sCfg.border} border rounded-xl px-3.5 py-2 text-xs font-semibold shadow-sm hover:shadow-md transition-all min-w-[140px]`}>
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`} />
                                                <span className={`${sCfg.text} flex-1 text-left`}>{sCfg.label}</span>
                                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${openStatutDrop === dropKey ? 'rotate-180' : ''}`} />
                                            </button>
                                            {openStatutDrop === dropKey && (
                                                <>
                                                    <div className="fixed inset-0 z-30" onClick={() => setOpenStatutDrop(null)} />
                                                    <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-40 min-w-[170px]">
                                                        {GM_STATUT_OPTIONS.map(o => (
                                                            <button key={o.value} onClick={() => { setOpenStatutDrop(null); handleSectionStatutChange('garde-meuble', item.type || '', o.value); }}
                                                                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-3 transition-colors ${currentStatut === o.value ? 'bg-gray-50 font-bold' : 'hover:bg-gray-50'}`}>
                                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${o.dot}`} />
                                                                <span className="text-gray-700 flex-1">{o.label}</span>
                                                                {currentStatut === o.value && <span className="text-[#1B365D]">&#10003;</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 10: Factures */}
                <FacturesSection clientId={id} hasGmActif={gmActif} />

                {/* Section 11: Solde — montants encaisses par les demenageurs sur le terrain */}
                <div className="bg-white rounded-xl border-2 border-amber-300 shadow-sm p-6" data-testid="section-solde">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                            <Euro className="w-4 h-4 text-emerald-600" />
                            <h2 className="text-base font-bold text-[#1B365D]">11. Solde</h2>
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs">Encaisse equipe terrain</Badge>
                        </div>
                        <Button
                            size="sm"
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1.5 h-8"
                            onClick={openSoldeCreate}
                            disabled={!canWriteClient}
                            title={!canWriteClient ? 'Accès refusé : permission Écriture requise' : 'Ajouter un paiement'}
                            data-testid="solde-add-btn"
                        >
                            <Plus className="w-4 h-4" /> Ajouter un paiement
                        </Button>
                    </div>
                    <div className="border-t border-gray-100 pt-5">
                        {(!client.soldes || client.soldes.length === 0) ? (
                            <p className="text-center text-gray-400 italic text-sm py-10">Aucun solde encaisse</p>
                        ) : (
                            <div className="space-y-3">
                                {client.soldes.map((sd) => (
                                    <div key={sd.id} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between bg-white" data-testid={`solde-row-${sd.id}`}>
                                        <div className="grid grid-cols-4 gap-6 flex-1">
                                            <div>
                                                <span className="text-xs text-gray-400">Date</span>
                                                <p className="text-sm font-medium text-gray-700">{sd.date ? new Date(sd.date + 'T00:00').toLocaleDateString('fr-FR') : '-'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400">Montant</span>
                                                <p className="text-sm font-bold text-gray-800">{sd.montant?.toFixed(2)} EUR</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400">Type</span>
                                                <p className="text-sm font-medium text-gray-700">{sd.type_paiement}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400">Remarque</span>
                                                <p className="text-sm text-gray-600">{sd.remarque || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 ml-3 shrink-0">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-gray-500 hover:text-[#1B365D] hover:bg-[#1B365D]/5"
                                                onClick={() => openSoldeEdit(sd)}
                                                disabled={!canWriteClient}
                                                title={!canWriteClient ? 'Accès refusé : permission Écriture requise' : 'Modifier ce solde'}
                                                data-testid={`solde-edit-${sd.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            {canDeleteClient && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => handleSoldeDelete(sd.id)}
                                                    title="Supprimer ce solde"
                                                    data-testid={`solde-delete-${sd.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="border-t border-dashed border-gray-200 pt-3 mt-3 flex justify-end">
                                    <div className="text-sm">
                                        <span className="text-gray-500">Total solde : </span>
                                        <span className="font-bold text-[#1B365D]" data-testid="solde-total">{client.soldes.reduce((s, a) => s + (a.montant || 0), 0).toFixed(2)} EUR</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cancel Client Dialog */}
            <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                <DialogContent className="max-w-md p-0" data-testid="cancel-client-modal">
                    <DialogTitle className="sr-only">Annuler client</DialogTitle>
                    <div className="p-6 space-y-4">
                        <h2 className="text-lg font-bold text-red-600">Annuler ce client</h2>
                        <p className="text-sm text-gray-500">Pourquoi le client a annule ?</p>
                        <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                            rows={3} placeholder="Raison de l'annulation..." data-testid="cancel-reason-input" />
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Annuler</Button>
                            <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={handleCancelClient} disabled={cancelSaving} data-testid="cancel-confirm-btn">
                                {cancelSaving ? 'Enregistrement...' : "Confirmer l'annulation"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={livRemarqueModal} onOpenChange={setLivRemarqueModal}>
                <DialogContent className="max-w-md p-0" data-testid="remarque-modal">
                    <DialogTitle className="sr-only">Remarque livraison</DialogTitle>
                    <div className="p-6 space-y-4">
                        <h2 className="text-lg font-bold text-[#1B365D]">Ajouter une remarque</h2>
                        <Textarea value={livRemarque} onChange={e => setLivRemarque(e.target.value)}
                            rows={3} placeholder="Remarque pour la livraison..." data-testid="liv-remarque-input" />
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setLivRemarqueModal(false)}>Annuler</Button>
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleConfirmLivraison} disabled={livSaving} data-testid="liv-remarque-confirm">
                                {livSaving ? 'Enregistrement...' : 'Valider'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Solde Add/Edit Dialog */}
            <Dialog open={!!soldeEdit} onOpenChange={(open) => { if (!open) setSoldeEdit(null); }}>
                <DialogContent className="max-w-md p-0" data-testid="solde-edit-modal">
                    <DialogTitle className="sr-only">{soldeEdit?.id ? 'Modifier le solde' : 'Ajouter un paiement'}</DialogTitle>
                    <div className="p-6 space-y-4">
                        <h2 className="text-lg font-bold text-[#1B365D]">{soldeEdit?.id ? 'Modifier le solde encaisse' : 'Ajouter un paiement'}</h2>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Date</label>
                                <Input
                                    type="date"
                                    value={soldeEdit?.date || ''}
                                    onChange={e => setSoldeEdit(s => ({ ...s, date: e.target.value }))}
                                    data-testid="solde-edit-date"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Montant (EUR)</label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    inputMode="decimal"
                                    value={soldeEdit?.montant || ''}
                                    onChange={e => setSoldeEdit(s => ({ ...s, montant: e.target.value }))}
                                    data-testid="solde-edit-montant"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Type de paiement</label>
                                <select
                                    className="w-full border border-gray-200 rounded-md h-10 px-3 text-sm bg-white"
                                    value={soldeEdit?.type_paiement || ''}
                                    onChange={e => setSoldeEdit(s => ({ ...s, type_paiement: e.target.value }))}
                                    data-testid="solde-edit-type"
                                >
                                    <option value="">Sélectionner…</option>
                                    {TYPE_PAIEMENT_SOLDE.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                    {soldeEdit?.type_paiement && !TYPE_PAIEMENT_SOLDE.includes(soldeEdit.type_paiement) && (
                                        <option value={soldeEdit.type_paiement}>{soldeEdit.type_paiement}</option>
                                    )}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Remarque</label>
                                <Textarea
                                    rows={2}
                                    value={soldeEdit?.remarque || ''}
                                    onChange={e => setSoldeEdit(s => ({ ...s, remarque: e.target.value }))}
                                    data-testid="solde-edit-remarque"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setSoldeEdit(null)} data-testid="solde-edit-cancel">Annuler</Button>
                            <Button
                                className="bg-[#C9A962] hover:bg-[#B8984F] text-white"
                                onClick={handleSoldeSave}
                                disabled={soldeSaving}
                                data-testid="solde-edit-save"
                            >
                                {soldeSaving ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Upload Dialog for Accorde status */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogContent className="max-w-md p-0" data-testid="upload-accorde-modal">
                    <DialogTitle className="sr-only">Justificatif</DialogTitle>
                    <div className="p-6 space-y-5">
                        <h2 className="text-lg font-bold text-[#1B365D]">Justificatif requis</h2>
                        <p className="text-sm text-gray-500">Pour passer au statut "Accorde", veuillez fournir un justificatif (photo ou document).</p>
                        <div className="border-2 border-dashed border-amber-300 rounded-xl p-5 bg-amber-50/30">
                            <label className="flex flex-col items-center gap-2 cursor-pointer">
                                <Upload className="w-6 h-6 text-amber-500" />
                                <span className="text-sm text-amber-700 font-medium">
                                    {uploadFile ? uploadFile.name : 'Upload ou photo obligatoire'}
                                </span>
                                <input type="file" className="hidden" accept="image/*,.pdf"
                                    onChange={e => setUploadFile(e.target.files[0])} data-testid="accorde-upload-input" />
                            </label>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Annuler</Button>
                            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleUploadAccorde} disabled={uploadSaving} data-testid="accorde-submit-btn">
                                {uploadSaving ? 'Enregistrement...' : 'Valider'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Apercu Modal */}
            <Dialog open={apercuOpen} onOpenChange={setApercuOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="apercu-modal">
                    <DialogTitle className="sr-only">Apercu du devis</DialogTitle>
                    {df && (
                        <div>
                            {/* Modal Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                <div>
                                    <h2 className="text-xl font-bold text-[#1B365D]">Devis – {FORMULE_LABELS[df.formule] || df.formule}</h2>
                                    <p className="text-sm text-gray-400">Details du devis</p>
                                </div>
                                <Button variant="outline" onClick={() => setApercuOpen(false)}>Fermer</Button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* 1. Infos client */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Users className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">1. Informations client</h3>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 text-sm">
                                        <div><span className="text-gray-400 text-xs">Nom</span>
                                            {editMode ? <Input value={editFields.nom} onChange={e => setEditFields({...editFields, nom: e.target.value})} className="mt-1" /> : <p className="font-semibold text-gray-800">{df.data?.nom || client.nom}</p>}
                                        </div>
                                        <div><span className="text-gray-400 text-xs">Telephone</span>
                                            {editMode ? <Input value={editFields.telephone} onChange={e => setEditFields({...editFields, telephone: e.target.value})} className="mt-1" /> : <p className="font-semibold text-gray-800">{df.data?.telephone || client.telephone || '-'}</p>}
                                        </div>
                                        <div><span className="text-gray-400 text-xs">Email</span>
                                            {editMode ? <Input value={editFields.email} onChange={e => setEditFields({...editFields, email: e.target.value})} className="mt-1" /> : <p className="font-semibold text-gray-800">{df.data?.email || client.email || '-'}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* 2. Adresses */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <MapPin className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">2. Adresses</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div><span className="text-emerald-500 text-xs font-bold">DEPART</span>
                                            {editMode ? <div className="mt-1"><AddressAutocomplete value={editFields.adresse_depart} onChange={val => setEditFields({...editFields, adresse_depart: val})} placeholder="Adresse de départ" testId="client-edit-adresse-depart" /></div> : <p className="text-gray-700">{df.data?.adresse_depart || '-'}</p>}
                                        </div>
                                        <div><span className="text-red-400 text-xs font-bold">ARRIVEE</span>
                                            {editMode ? <div className="mt-1"><AddressAutocomplete value={editFields.adresse_arrivee} onChange={val => setEditFields({...editFields, adresse_arrivee: val})} placeholder="Adresse d'arrivée" testId="client-edit-adresse-arrivee" /></div> : <p className="text-gray-700">{df.data?.adresse_arrivee || '-'}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* 3. Dates */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Clock className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">3. Dates et details</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {[
                                            ['date_chargement', 'Date chargement'],
                                            ['date_dechargement', 'Date dechargement'],
                                            ['etage_depart', 'Etage depart'],
                                            ['etage_arrivee', 'Etage arrivee'],
                                        ].map(([key, label]) => (
                                            <div key={key}><span className="text-gray-400 text-xs">{label}</span>
                                                {editMode ? <Input value={editFields[key] || ''} onChange={e => setEditFields({...editFields, [key]: e.target.value})} className="mt-1" type={key.startsWith('date') ? 'date' : 'text'} /> : <p className="text-gray-700">{df.data?.[key] || '-'}</p>}
                                            </div>
                                        ))}
                                        {/* Ascenseur dropdowns */}
                                        {[
                                            ['ascenseur_depart', 'Ascenseur depart'],
                                            ['ascenseur_arrivee', 'Ascenseur arrivee'],
                                        ].map(([key, label]) => (
                                            <div key={key}><span className="text-gray-400 text-xs">{label}</span>
                                                {editMode ? (
                                                    <select value={editFields[key] || ''} onChange={e => setEditFields({...editFields, [key]: e.target.value})}
                                                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200">
                                                        <option value="">-</option>
                                                        {ASCENSEUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                ) : (
                                                    <p className="text-gray-700">{ASCENSEUR_OPTIONS.find(o => o.value === df.data?.[key])?.label || df.data?.[key] || '-'}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 4. AOT / Monte-meuble */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <FileText className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">4. AOT / Monte-meuble / Garde-meuble</h3>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 text-sm">
                                        {[
                                            ['aot_depart', 'AOT depart'],
                                            ['aot_arrivee', 'AOT arrivee'],
                                            ['monte_meuble_depart', 'Monte-meuble depart'],
                                            ['monte_meuble_arrivee', 'Monte-meuble arrivee'],
                                        ].map(([key, label]) => (
                                            <div key={key}><span className="text-gray-400 text-xs">{label}</span>
                                                {editMode ? (
                                                    <select value={editFields[key] || ''} onChange={e => setEditFields({...editFields, [key]: e.target.value})} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                                        <option value="">-</option><option value="oui">Oui</option><option value="non">Non</option>
                                                    </select>
                                                ) : <p className="text-gray-700">{df.data?.[key] || '-'}</p>}
                                            </div>
                                        ))}
                                        <div><span className="text-gray-400 text-xs">Garde-meuble</span>
                                            {editMode ? (
                                                <select value={editFields.garde_meuble || 'non'} onChange={e => setEditFields({...editFields, garde_meuble: e.target.value})} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                                    <option value="oui">Oui</option><option value="non">Non</option>
                                                </select>
                                            ) : <p className="text-gray-700">{df.data?.garde_meuble || '-'}</p>}
                                        </div>
                                        {(editMode ? editFields.garde_meuble === 'oui' : df.data?.garde_meuble === 'oui') && (
                                            <>
                                                <div><span className="text-gray-400 text-xs">Duree</span>
                                                    {editMode ? <Input value={editFields.garde_meuble_duree || ''} onChange={e => setEditFields({...editFields, garde_meuble_duree: e.target.value})} className="mt-1" /> : <p className="text-gray-700">{df.data?.garde_meuble_duree || '-'}</p>}
                                                </div>
                                                <div><span className="text-gray-400 text-xs">Mensualite</span>
                                                    {editMode ? <Input value={editFields.garde_meuble_mensualite || ''} onChange={e => setEditFields({...editFields, garde_meuble_mensualite: e.target.value})} className="mt-1" type="number" /> : <p className="text-gray-700">{df.data?.garde_meuble_mensualite || '-'} EUR</p>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* 4.5 Fournitures */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Package className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">4.5 Fournitures</h3>
                                    </div>
                                    <div className="text-sm">
                                        <span className="text-gray-400 text-xs">Fournitures</span>
                                        {editMode ? (
                                            <select value={editFields.fournitures || 'non'} onChange={e => setEditFields({...editFields, fournitures: e.target.value})} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                                <option value="oui">Oui</option><option value="non">Non</option>
                                            </select>
                                        ) : <p className="text-gray-700">{df.data?.fournitures || '-'}</p>}
                                    </div>
                                </div>

                                {/* 5. Volume / Distance */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <FileText className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">5. Volume et distance</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div><span className="text-gray-400 text-xs">Volume</span><p className="text-gray-700">{df.volume ? `${df.volume} m3` : '-'}</p></div>
                                        <div><span className="text-gray-400 text-xs">Distance</span><p className="text-gray-700">{df.distance_km ? `${df.distance_km} km` : '-'}</p></div>
                                    </div>
                                </div>

                                {/* Remarque prospect (suit le client - lecture seule) */}
                                {(client?.remarques || '').trim() && (
                                    <div className="border border-gray-200 rounded-xl p-5" data-testid="client-modal-remarque-prospect">
                                        <div className="flex items-center gap-2 mb-4">
                                            <FileText className="w-4 h-4 text-emerald-600" />
                                            <h3 className="text-sm font-bold text-[#1B365D]">Remarque prospect</h3>
                                            <span className="ml-1 inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                Synchronisée avec le prospect
                                            </span>
                                        </div>
                                        <div className="border-l-2 border-l-emerald-400 pl-4 py-2 bg-emerald-50/40 rounded-r-lg">
                                            <p className="text-sm text-[#0F172A] whitespace-pre-wrap break-words">{client.remarques}</p>
                                        </div>
                                    </div>
                                )}

                                {/* 6. Remarques */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <FileText className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">6. Remarques devis ({FORMULE_LABELS[df.formule] || df.formule})</h3>
                                    </div>
                                    {editMode ? (
                                        <Textarea value={editRemarques} onChange={e => setEditRemarques(e.target.value)}
                                            rows={3} className="border-l-2 border-l-red-300" />
                                    ) : (
                                        <div className="border-l-2 border-l-red-300 pl-4 py-2 bg-gray-50 rounded-r-lg">
                                            <p className="text-sm text-gray-600">{df.data?.remarques || 'rien a signaler'}</p>
                                        </div>
                                    )}
                                </div>

                                {/* 7. Prix final */}
                                <div className="border border-gray-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-5">
                                        <DollarSign className="w-4 h-4 text-[#1B365D]" />
                                        <h3 className="text-sm font-bold text-[#1B365D]">7. Prix final</h3>
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    </div>

                                    {/* Main prestation */}
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-1 h-8 bg-emerald-400 rounded-full" />
                                        <span className="text-sm font-semibold text-gray-700 flex-1">
                                            Prestation demenagement - {FORMULE_LABELS[df.formule] || df.formule}
                                        </span>
                                        {editMode ? (
                                            <div className="flex items-center gap-1">
                                                <Input value={editPrix} onChange={e => setEditPrix(e.target.value)}
                                                    className="w-24 text-right" type="number" />
                                                <span className="text-xs text-gray-400">EUR HT</span>
                                            </div>
                                        ) : (
                                            <span className="text-sm font-bold text-gray-800">{df.data?.prix_prestation || df.total_ht} EUR HT</span>
                                        )}
                                    </div>

                                    {/* Extra prestations */}
                                    {(editMode ? editPrestations : (df.data?.prestations || [])).map((p, idx) => (
                                        <div key={idx} className="flex items-center gap-3 mb-4 ml-3">
                                            <div className="w-1 h-10 bg-emerald-400 rounded-full" />
                                            <span className="text-sm text-gray-700 flex-1 font-medium">{p.name || '-'}</span>
                                            {editMode ? (
                                                <div className="flex items-center gap-2">
                                                    <Input value={p.prix} onChange={e => {
                                                        const upd = [...editPrestations];
                                                        upd[idx] = { ...upd[idx], prix: e.target.value };
                                                        setEditPrestations(upd);
                                                    }} className="w-24 text-right rounded-xl border-gray-200 bg-gray-50" type="number" />
                                                    <span className="text-xs text-gray-400">EUR HT</span>
                                                    <button onClick={() => setEditPrestations(editPrestations.filter((_, i) => i !== idx))}
                                                        className="p-1.5 text-gray-300 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            ) : (
                                                <span className="text-sm font-semibold text-gray-700">{p.prix} EUR HT</span>
                                            )}
                                        </div>
                                    ))}

                                    {/* Add prestation button with dropdown */}
                                    {editMode && (
                                        <div className="relative mt-3">
                                            <button onClick={() => { setPresDropOpen(!presDropOpen); setShowCustomInput(false); }}
                                                className="w-full py-3 border-2 border-dashed border-orange-200 rounded-xl text-sm text-orange-400 hover:border-orange-300 hover:text-orange-500 transition-colors flex items-center justify-center gap-1">
                                                <Plus className="w-4 h-4" /> Prestation additionnelle
                                            </button>
                                            {presDropOpen && (
                                                <>
                                                    <div className="fixed inset-0 z-30" onClick={() => { setPresDropOpen(false); setShowCustomInput(false); }} />
                                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-40 w-64">
                                                        {PRESTATION_OPTIONS.map(opt => (
                                                            <button key={opt} onClick={() => {
                                                                setEditPrestations([...editPrestations, { name: opt, prix: '0' }]);
                                                                setPresDropOpen(false);
                                                            }}
                                                                className="w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                                                {opt}
                                                            </button>
                                                        ))}
                                                        <div className="border-t border-gray-100 mt-1 pt-1">
                                                            {showCustomInput ? (
                                                                <div className="px-4 py-2 flex items-center gap-2">
                                                                    <Input value={customPresName} onChange={e => setCustomPresName(e.target.value)}
                                                                        placeholder="Nom personnalise..." className="text-sm flex-1"
                                                                        onKeyDown={e => {
                                                                            if (e.key === 'Enter' && customPresName.trim()) {
                                                                                setEditPrestations([...editPrestations, { name: customPresName.trim(), prix: '0' }]);
                                                                                setCustomPresName('');
                                                                                setPresDropOpen(false);
                                                                                setShowCustomInput(false);
                                                                            }
                                                                        }} autoFocus />
                                                                    <button onClick={() => {
                                                                        if (customPresName.trim()) {
                                                                            setEditPrestations([...editPrestations, { name: customPresName.trim(), prix: '0' }]);
                                                                            setCustomPresName('');
                                                                            setPresDropOpen(false);
                                                                            setShowCustomInput(false);
                                                                        }
                                                                    }} className="text-blue-500 text-sm font-semibold hover:text-blue-600">OK</button>
                                                                </div>
                                                            ) : (
                                                                <button onClick={() => setShowCustomInput(true)}
                                                                    className="w-full text-left px-5 py-3 text-sm text-blue-500 font-semibold hover:bg-blue-50 transition-colors">
                                                                    + Personnalise
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Totals */}
                                    <div className="mt-5 pt-4 border-t border-dashed border-gray-200 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Total HT</span>
                                            <span className="font-semibold">{editMode ? computedTotalHt.toFixed(2) : (df.total_ht || 0).toFixed(2)} EUR</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">TVA (20%)</span>
                                            <span>{editMode ? computedTva.toFixed(2) : (df.tva || 0).toFixed(2)} EUR</span>
                                        </div>
                                        <div className="flex justify-between text-base font-bold mt-1">
                                            <span className="text-[#1B365D]">Total TTC</span>
                                            <span className="text-[#1B365D]">{editMode ? computedTotalTtc.toFixed(2) : (df.total_ttc || 0).toFixed(2)} EUR</span>
                                        </div>
                                    </div>
                                </div>


                                {/* Historique des versions */}
                                {client.devis_history?.length > 0 && (
                                    <div className="border border-gray-200 rounded-xl p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Clock className="w-4 h-4 text-[#1B365D]" />
                                            <h3 className="text-sm font-bold text-[#1B365D]">Historique des versions</h3>
                                        </div>
                                        <div className="space-y-2">
                                            {client.devis_history.map((h) => (
                                                <div key={h.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5 text-sm">
                                                    <div className="flex items-center gap-3">
                                                        <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-xs">v{h.version}</Badge>
                                                        <span className="text-gray-600 font-medium">{h.numero}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-gray-500">{h.total_ttc?.toFixed(2)} EUR</span>
                                                        <span className="text-xs text-gray-400">{h.archived_at ? new Date(h.archived_at).toLocaleDateString('fr-FR') : ''}</span>
                                                        <Badge className="bg-gray-100 text-gray-400 border border-gray-200 text-[10px]">Lecture seule</Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                                {editMode ? (
                                    <>
                                        <Button variant="outline" onClick={() => setEditMode(false)}>Annuler</Button>
                                        <Button className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-2" onClick={handleSaveDevis} disabled={saving}>
                                            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-2" onClick={() => setEditMode(true)} data-testid="modifier-btn">
                                        <Pencil className="w-4 h-4" /> Modifier
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Day toggle confirmation dialog */}
            <Dialog open={dayDialog.open} onOpenChange={(o) => !dayDialog.busy && setDayDialog(d => ({ ...d, open: o }))}>
                <DialogContent className="sm:max-w-md" data-testid="day-toggle-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D]">
                            {dayDialog.willEnable ? 'Planifier la prestation' : 'Retirer la planification'}
                        </DialogTitle>
                        <DialogDescription>
                            {dayDialog.willEnable
                                ? `${dayDialog.label} est nécessaire pour la date ${formatDate(dayDialog.date)} (J${dayDialog.jourIndex}). Confirmer ?`
                                : `${dayDialog.label} ne sera plus planifié pour le ${formatDate(dayDialog.date)} (J${dayDialog.jourIndex}). Le statut passera à "non planifiée". Confirmer ?`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
                        <Button
                            type="button" variant="outline"
                            onClick={() => setDayDialog({ open: false, kind: '', label: '', date: '', jourIndex: 0, willEnable: true, busy: false })}
                            disabled={dayDialog.busy}
                            data-testid="day-toggle-cancel"
                        >
                            Annuler
                        </Button>
                        <Button
                            type="button"
                            onClick={confirmToggleDay}
                            disabled={dayDialog.busy}
                            className={dayDialog.willEnable ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
                            data-testid="day-toggle-confirm"
                        >
                            {dayDialog.busy ? 'En cours...' : (dayDialog.willEnable ? 'Confirmer' : 'Retirer')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
